/**
 * File: historicInvoices.ts
 * Path: packages/config/src/invoices/historicInvoices.ts
 * Description: Pairing the payments of a contract brought in from before
 * with the invoices the books already hold for them (the Zoho history).
 * Nothing is copied: the pairing is only a link,
 * contracts/<id>/invoiceLinks/<payment reference> = { journalId, number }.
 * The number, date and amount are always read from the books entry.
 *
 * The rule, in tiers (never on an amount being "big enough"):
 * 1. Linked without asking: the payment's money matches exactly one Zoho
 *    payment of the same client (same amount, within 3 days), Zoho applied
 *    that payment to exactly one invoice, and that invoice is for exactly
 *    what the contract scheduled. Zoho's own allocation is the evidence.
 * 2. Suggested, one tap to confirm: the matching Zoho payment paid an
 *    invoice of a different size (a part payment, or an invoice covering
 *    two payments), or no Zoho payment matches but invoices of the same
 *    client for exactly the scheduled amount were issued in the weeks
 *    before the money arrived.
 * 3. Nothing: Liam picks by hand, or leaves it.
 * Before all of these: a payment marked received here whose own books entry
 * paid exactly one of the client's invoices is linked to that invoice. That
 * entry is the evidence (a deposit and its settlement may share one invoice).
 *
 * Re-running it on a contract that was linked under the old rule shows each
 * change against today's links before anything is saved. Pure: the server
 * reads the records, calls this, and writes only the links Liam confirms.
 */

import type { Journal } from "../accounting/accountingTypes";
import type { PaymentReceipt } from "../contracts/contractReceipts";
import { invoiceNumbersIn } from "./invoiceLedger";

/** A Zoho invoice as the books hold it. */
export interface BooksInvoice {
  journalId: string;
  /** "INV-000012" */
  number: string;
  /** YYYY-MM-DD */
  date: string;
  totalCents: number;
}

/** What is stored under a contract for one payment: only where to find the invoice. */
export interface HistoricInvoiceLink {
  /** The payment reference; also the document id. */
  reference: string;
  journalId: string;
  number: string;
}

export const toHistoricInvoiceLink = (
  id: string,
  data: Record<string, unknown> | null | undefined,
): HistoricInvoiceLink | null => {
  const journalId = typeof data?.journalId === "string" ? data.journalId : "";
  const number = typeof data?.number === "string" ? data.number : "";
  return journalId && number ? { reference: id, journalId, number } : null;
};

export interface HistoricPayment {
  reference: string;
  label: string;
  /** Whole cents the contract schedules for it. */
  cents: number;
}

/** A Zoho customer payment in the books, and the invoices Zoho applied it to. */
export interface ZohoPaymentEvidence {
  journalId: string;
  date: string;
  amountCents: number;
  invoices: string[];
}

/** The Zoho payments among a client's journals: what each paid, from its tagged lines or its narration. */
export const zohoPaymentEvidence = (journals: readonly Journal[], debtorIds: ReadonlySet<string>): ZohoPaymentEvidence[] =>
  journals.flatMap((j) => {
    if (!(j.externalRef ?? "").startsWith("zoho:payment:") || j.reversedBy || j.source === "reversal") return [];
    const tagged = j.lines.filter((l) => debtorIds.has(l.accountId) && l.invoice).map((l) => l.invoice as string);
    const invoices = tagged.length > 0 ? [...new Set(tagged)] : invoiceNumbersIn(j.narration);
    return [{ journalId: j.id, date: j.date, amountCents: j.totalCents, invoices }];
  });

const DAY_MS = 86_400_000;
const dayNumber = (iso: string) => Date.parse(`${iso}T00:00:00Z`) / DAY_MS;

/** A receipt and a Zoho payment this many days apart can still be the same money. */
export const EVIDENCE_DAYS = 3;
/** Without evidence, an invoice may be issued this many days before the money arrived, and after. */
export const BEFORE_DAYS = 62;
export const AFTER_DAYS = 10;

export type RelinkTier = "auto" | "suggest" | "none";
/** What applying would do to today's link. */
export type RelinkAction = "keep" | "add" | "change" | "remove" | "none";

export interface RelinkOption {
  number: string;
  date: string;
  totalCents: number;
  why: string;
}

export interface RelinkRow {
  reference: string;
  label: string;
  scheduledCents: number;
  /** What arrived and when; null while not marked received. */
  paidCents: number | null;
  paidOn: string | null;
  /** The invoice linked today, if any. */
  current: string | null;
  tier: RelinkTier;
  /** What the rule links without asking (tier auto). */
  proposed: string | null;
  /** One-tap choices (tier suggest), best first. */
  options: RelinkOption[];
  why: string;
  action: RelinkAction;
}

export interface RelinkPlan {
  rows: RelinkRow[];
  /** The client's invoices no payment here takes or is offered (they may belong to another contract). */
  leftOver: BooksInvoice[];
}

export const planRelink = (input: {
  payments: readonly HistoricPayment[];
  receipts: readonly PaymentReceipt[];
  /** The client's invoices in the books that no other contract's link and no saved invoice has taken. */
  invoices: readonly BooksInvoice[];
  /** The client's Zoho payments. */
  evidence: readonly ZohoPaymentEvidence[];
  /** Today's links on this contract: payment reference to invoice number. */
  current: ReadonlyMap<string, string>;
  /** What each payment's own books entry (crm:payment) paid: payment reference to invoice numbers. */
  booked?: ReadonlyMap<string, readonly string[]>;
}): RelinkPlan => {
  const byReference = new Map(input.receipts.map((r) => [r.reference, r]));
  const byNumber = new Map(input.invoices.map((i) => [i.number, i]));
  const taken = new Set<string>();
  const offered = new Set<string>();
  const usedEvidence = new Set<string>();

  const ordered = [...input.payments].sort((a, b) => {
    const ra = byReference.get(a.reference)?.receivedOn ?? "9999";
    const rb = byReference.get(b.reference)?.receivedOn ?? "9999";
    return ra.localeCompare(rb) || a.reference.localeCompare(b.reference);
  });

  const option = (number: string, why: string): RelinkOption[] => {
    const inv = byNumber.get(number);
    return inv ? [{ number, date: inv.date, totalCents: inv.totalCents, why }] : [];
  };

  const decided = ordered.map((payment) => {
    const receipt = byReference.get(payment.reference) ?? null;
    const paidCents = receipt ? Math.round(receipt.amount * 100) : null;
    const base = {
      reference: payment.reference,
      label: payment.label,
      scheduledCents: payment.cents,
      paidCents,
      paidOn: receipt?.receivedOn ?? null,
      current: input.current.get(payment.reference) ?? null,
    };
    if (!receipt || paidCents === null) {
      const keep = base.current ? option(base.current, "Linked before. Keep it only if this invoice is for this payment.") : [];
      return { ...base, tier: (keep.length > 0 ? "suggest" : "none") as RelinkTier, proposed: null, options: keep, why: "Not marked received yet." };
    }
    const paidInBooks = (input.booked?.get(payment.reference) ?? []).filter((n) => byNumber.has(n));
    if (paidInBooks.length === 1) {
      // Not "taken": the same invoice may also be another payment's (deposit and settlement on one invoice).
      return {
        ...base,
        tier: "auto" as RelinkTier,
        proposed: paidInBooks[0],
        options: [],
        why: `The books entry for this payment pays ${paidInBooks[0]}.`,
      };
    }
    const arrived = dayNumber(receipt.receivedOn);
    const near = input.evidence
      .filter((e) => !usedEvidence.has(e.journalId) && e.amountCents === paidCents && Math.abs(dayNumber(e.date) - arrived) <= EVIDENCE_DAYS)
      .sort((a, b) => Math.abs(dayNumber(a.date) - arrived) - Math.abs(dayNumber(b.date) - arrived) || a.date.localeCompare(b.date));
    const closest = near[0];
    const tied = closest ? near.filter((e) => Math.abs(dayNumber(e.date) - arrived) === Math.abs(dayNumber(closest.date) - arrived)) : [];
    if (closest && tied.length === 1) {
      usedEvidence.add(closest.journalId);
      const paidInvoices = closest.invoices.filter((n) => byNumber.has(n) && !taken.has(n));
      const only = paidInvoices.length === 1 ? byNumber.get(paidInvoices[0])! : null;
      if (only && only.totalCents === payment.cents) {
        taken.add(only.number);
        return {
          ...base,
          tier: "auto" as RelinkTier,
          proposed: only.number,
          options: [],
          why: `Zoho applied the matching payment of ${closest.date} to ${only.number}, an invoice for exactly this payment.`,
        };
      }
      if (paidInvoices.length > 0) {
        paidInvoices.forEach((n) => offered.add(n));
        return {
          ...base,
          tier: "suggest" as RelinkTier,
          proposed: null,
          options: paidInvoices.flatMap((n) =>
            option(n, `Zoho applied the matching payment of ${closest.date} to it, but it is for a different amount than this payment.`),
          ),
          why: "The money matches a Zoho payment, but its invoice isn't for exactly this payment. Confirm it if it is.",
        };
      }
    }
    const lo = arrived - BEFORE_DAYS;
    const hi = arrived + AFTER_DAYS;
    const sameAmount = input.invoices
      .filter((i) => !taken.has(i.number) && i.totalCents === payment.cents && dayNumber(i.date) >= lo && dayNumber(i.date) <= hi)
      .sort((a, b) => Math.abs(dayNumber(a.date) - arrived) - Math.abs(dayNumber(b.date) - arrived) || a.number.localeCompare(b.number));
    if (sameAmount.length > 0) {
      sameAmount.forEach((i) => offered.add(i.number));
      return {
        ...base,
        tier: "suggest" as RelinkTier,
        proposed: null,
        options: sameAmount.map((i) => ({
          number: i.number,
          date: i.date,
          totalCents: i.totalCents,
          why: "No Zoho payment matches the money, but this invoice of the same client is for exactly this amount.",
        })),
        why:
          tied.length > 1
            ? "Two Zoho payments match the money equally well, so pick the invoice."
            : "No Zoho payment matches the money. Pick the invoice if one of these is it.",
      };
    }
    const keep = base.current && !taken.has(base.current) ? option(base.current, "Linked before under the old rule; nothing in the books confirms it.") : [];
    return {
      ...base,
      tier: (keep.length > 0 ? "suggest" : "none") as RelinkTier,
      proposed: null,
      options: keep,
      why: "No invoice in the books is clearly for this payment.",
    };
  });

  const rows: RelinkRow[] = decided
    .map((r) => {
      const action: RelinkAction =
        r.proposed !== null
          ? r.current === r.proposed
            ? "keep"
            : r.current
              ? "change"
              : "add"
          : r.current
            ? "remove"
            : "none";
      return { ...r, action };
    })
    .sort((a, b) => a.reference.localeCompare(b.reference));

  return {
    rows,
    leftOver: input.invoices.filter((i) => !taken.has(i.number) && !offered.has(i.number)),
  };
};

/**
 * What a row becomes when nobody taps anything: the rule's own pick, or
 * today's link when it is still one of the offered choices. Anything else
 * is left unlinked until Liam picks it.
 */
export const defaultRelinkChoice = (row: Pick<RelinkRow, "proposed" | "current" | "options">): string | null =>
  row.proposed ?? (row.current && row.options.some((o) => o.number === row.current) ? row.current : null);

/** What the linkContractInvoices callable reports, for a dry run and for apply alike. */
export interface LinkInvoicesResult {
  /** True when links were saved. */
  applied: boolean;
  rows: RelinkRow[];
  leftOver: Array<{ number: string; date: string; totalCents: number }>;
  /** On apply: how many links were added, changed and removed. */
  saved: { added: number; changed: number; removed: number };
}
